import { Context } from '@nuxt/types'
import * as env from '@ustra/core/src/config/env'
import * as webUtils from '@ustra/core/src/utils/web-utils'
import * as axiosUtils from '@ustra/core/src/utils/axios-utils'
import CookieStorage from '@ustra/core/src/utils/storage/cookie-storage'
import * as cryptoUtils from '@ustra/core/src/utils/client/crypto-utils'
import { getGlobalObject } from '@ustra/core/src/utils/core-utils'
import { getConfigEnv as getConfigEnvFromStore, removeClientProfileInfo, getProperties as getPropertiesFromStore, getProfileProperties } from '../../../config/nuxt-property-store'
import NuxtAppProperties from '../../../config/nuxt-app-properties'

export class Env {
  private context: Context = null
  private encryptedProfileString: string = null
  // private profile: env.Profile = null
  public isMobileRequest: boolean = false
  public isAndroidRequest: boolean = false
  public isIOsRequest: boolean = false
  public appProp: NuxtAppProperties = null

  constructor(context: Context) {
    this.context = context
  }

  async init() {
    try {
      this.disableConsole(getGlobalObject())

      const configEnv = await this.getConfigEnv()
      env.utils.updateCurrentProfile(configEnv)

      if (this.context.isClient) {
        removeClientProfileInfo(this.context)
      }

      this.isMobileRequest = webUtils.isMobileRequest(this.context.req)
      this.isAndroidRequest = webUtils.isAndroidRequest(this.context.req)
      this.isIOsRequest = webUtils.isIOsRequest(this.context.req)
      this.appProp = Object.freeze(this.getProperties(this.currentProfile))

      // bind context path
      this.context.$ustra.contextPath = this.appProp.server.contextPath || '/'
    } finally {
      this.enableConsole(getGlobalObject())
    }
  }

  private async getConfigEnv() {
    // for static application

    if (this.context.isStatic) {
      try {
        const cookieStorage = new CookieStorage({
          keyConverter: null,
          valueConverter: null,
        })

        let encProfile = cookieStorage.getItem('c', this.context.req, this.context.res)

        if (!encProfile) {
          // API 호출 케이스
          encProfile = (
            await axiosUtils.call({
              url: '/api/current-profile',
            })
          ).data as string
        }

        const decProfile = cryptoUtils.decryptAes256(cryptoUtils.decryptAes256(encProfile, process.env.SERVER_PROP_ENC_KEY), process.env.SERVER_PROP_ENC_KEY)
        return decProfile
      } catch (e) {
        return getConfigEnvFromStore()
      }
    } else {
      return getConfigEnvFromStore()
    }
  }

  private getProperties(profile) {
    if (this.context.isStatic) {
      return getProfileProperties(profile)
    } else {
      return getPropertiesFromStore()
    }
  }

  /**
   * 현재 프로파일 조회
   */
  get currentProfile() {
    return env.utils.currentProfile()
  }

  /**
   * 암호화된 프로파일 조회
   */
  get encryptedProfile() {
    if (!this.currentProfile) {
      return null
    }

    if (!this.encryptedProfileString) {
      const serverProfile = env.utils.getServerProfileName(this.currentProfile)
      this.encryptedProfileString = cryptoUtils.encryptAes256(cryptoUtils.encryptAes256(serverProfile, process.env.SERVER_PROP_ENC_KEY), process.env.SERVER_PROP_ENC_KEY)
    }

    return this.encryptedProfileString
  }

  /**
   * 운영 환경 여부
   */
  get isProduction() {
    return env.utils.isProduction()
  }

  /**
   * 개발 환경 여부
   */
  get isDev() {
    return env.utils.isDev()
  }

  /**
   * 스테이징 여부
   */
  get isStaging() {
    return env.utils.isStaging()
  }

  /**
   * 로컬 환경 여부
   */
  get isLocal() {
    return env.utils.isLocal()
  }

  /**
   * 개발 서버 구동 여부
   */
  get isDevServer() {
    return env.utils.isDevServer()
  }

  // #region console 관련 기능
  private disableConsole(win: any) {
    if (!this.context.isClient) {
      return
    }

    if (!win.originConsole) {
      win.originConsole = win.console
    }
    win.console = {
      log: () => {},
      debug: () => {},
      info: () => {},
      warn: () => {},
      error: () => {},
      dir: () => {},
    }
  }

  private enableConsole = (win: any) => {
    if (this.context.isClient && !this.appProp.logger?.disableBrowserConsole) {
      if (win.originConsole) {
        win.console = win.originConsole
      }
    }
  }
  // //#endregion
}
